import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Lock, User, MapPin, Briefcase, IndianRupee, Users as UsersIcon, Tag } from "lucide-react";
import { CATEGORIES, CATEGORY_COLORS } from "../../constants/scenarioCategories";

const EMPTY_FORM = {
  name: "", name_hi: "", description: "", description_hi: "", difficulty: "medium", category: "other",
  customer_profile: { age: 35, gender: "Male", profession: "", location: "", job_role: "", salary: "", family_details: "" },
};

function getDifficultyColor(d) {
  if (d === "easy") return "bg-[#14B8A6]/10 text-[#14B8A6]";
  if (d === "hard") return "bg-red-50 text-red-600";
  return "bg-[#F59E0B]/10 text-[#F59E0B]";
}

export function ScenarioManagement({ defaults, customs, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowForm(true);
  };

  const openEdit = (scenario) => {
    setEditing(scenario);
    setForm({
      name: scenario.name || "",
      name_hi: scenario.name_hi || "",
      description: scenario.description || "",
      description_hi: scenario.description_hi || "",
      difficulty: scenario.difficulty || "medium",
      category: scenario.category || "other",
      customer_profile: {
        age: scenario.customer_profile?.age || 35,
        gender: scenario.customer_profile?.gender || "Male",
        profession: scenario.customer_profile?.profession || "",
        location: scenario.customer_profile?.location || "",
        job_role: scenario.customer_profile?.job_role || "",
        salary: scenario.customer_profile?.salary || "",
        family_details: scenario.customer_profile?.family_details || "",
      },
    });
    setError("");
    setShowForm(true);
  };

  const updateProfile = (field, value) => {
    setForm((f) => ({ ...f, customer_profile: { ...f.customer_profile, [field]: value } }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      setError("Name and description are required");
      return;
    }
    const cp = form.customer_profile;
    if (!cp.profession || !cp.location || !cp.job_role || !cp.salary || !cp.family_details) {
      setError("All customer profile fields are required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, customer_profile: { ...cp, age: Number(cp.age) || 35 } };
      if (editing) {
        await axios.put(`${API}/admin/scenarios/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/admin/scenarios`, payload);
      }
      setShowForm(false);
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save scenario");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this custom scenario? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/scenarios/${id}`);
      onRefresh();
    } catch (e) {
      console.error("[Admin] Failed to delete scenario:", e);
    }
  };

  const handleToggle = async (scenario) => {
    try {
      await axios.put(`${API}/admin/scenarios/${scenario.id}`, { is_active: !scenario.is_active });
      onRefresh();
    } catch (e) {
      console.error("[Admin] Failed to toggle scenario:", e);
    }
  };

  const allScenarios = [...defaults, ...customs];
  const usedCategories = [...new Set(allScenarios.map((s) => s.category || "other"))];

  const filteredDefaults = categoryFilter === "all" ? defaults : defaults.filter((s) => (s.category || "other") === categoryFilter);
  const filteredCustoms = categoryFilter === "all" ? customs : customs.filter((s) => (s.category || "other") === categoryFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#0B1F3B]">Scenario Management</h2>
          <p className="text-sm text-slate-500">Create custom practice scenarios with customer profiles</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 btn-premium" data-testid="create-scenario-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Scenario
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="category-filter">
        <button onClick={() => setCategoryFilter("all")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${categoryFilter === "all" ? "bg-[#0B1F3B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          data-testid="filter-all">
          All
        </button>
        {Object.entries(CATEGORIES).filter(([id]) => usedCategories.includes(id)).map(([id, label]) => (
          <button key={id} onClick={() => setCategoryFilter(id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${categoryFilter === id ? "bg-[#0B1F3B] text-white" : `${CATEGORY_COLORS[id]} hover:opacity-80`}`}
            data-testid={`filter-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Default Scenarios */}
      <div>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Default Scenarios ({filteredDefaults.length})</h3>
        <div className="grid gap-3">
          {filteredDefaults.map((s) => (
            <Card key={s.id} className="border-slate-200/60">
              <CardContent className="py-4 px-5 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-[#0B1F3B]">{s.name}</h4>
                    <Badge variant="outline" className={getDifficultyColor(s.difficulty)}>{s.difficulty}</Badge>
                    <Badge variant="outline" className={CATEGORY_COLORS[s.category || "other"] + " text-[10px]"}>
                      <Tag className="w-3 h-3 mr-1" />{CATEGORIES[s.category || "other"]}
                    </Badge>
                    <Badge variant="outline" className="bg-slate-50 text-slate-400 text-[10px]"><Lock className="w-3 h-3 mr-1" />Default</Badge>
                  </div>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-1">{s.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Custom Scenarios */}
      <div>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Custom Scenarios ({filteredCustoms.length})
        </h3>
        {filteredCustoms.length === 0 ? (
          <Card className="border-dashed border-slate-300">
            <CardContent className="py-10 text-center">
              <p className="text-slate-400">{categoryFilter === "all" ? 'No custom scenarios yet. Click "Create Scenario" to add one.' : `No custom scenarios in ${CATEGORIES[categoryFilter] || categoryFilter}.`}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredCustoms.map((s) => (
              <Card key={s.id} className={`border-slate-200/60 ${!s.is_active ? "opacity-60" : ""}`} data-testid={`custom-scenario-${s.id}`}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-[#0B1F3B]">{s.name}</h4>
                        <Badge variant="outline" className={getDifficultyColor(s.difficulty)}>{s.difficulty}</Badge>
                        <Badge variant="outline" className={CATEGORY_COLORS[s.category || "other"] + " text-[10px]"}>
                          <Tag className="w-3 h-3 mr-1" />{CATEGORIES[s.category || "other"]}
                        </Badge>
                        <Badge variant="outline" className="bg-[#2563EB]/5 text-[#2563EB] text-[10px]">Custom</Badge>
                        {!s.is_active && <Badge variant="outline" className="bg-red-50 text-red-500 text-[10px]">Inactive</Badge>}
                      </div>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{s.description}</p>
                      {s.customer_profile && (
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{s.customer_profile.gender}, {s.customer_profile.age}y</span>
                          <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{s.customer_profile.job_role}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.customer_profile.location}</span>
                          <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{s.customer_profile.salary}</span>
                          <span className="flex items-center gap-1"><UsersIcon className="w-3 h-3" />{s.customer_profile.family_details}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleToggle(s)} className="text-xs"
                        data-testid={`toggle-scenario-${s.id}`}>
                        {s.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-scenario-${s.id}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} data-testid={`delete-scenario-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0B1F3B]">{editing ? "Edit Scenario" : "Create Custom Scenario"}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {editing ? "Update the scenario details and customer profile below." : "Define a practice scenario with a customer profile for AI role-play."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {error && <p className="text-sm text-red-500 bg-red-50 p-3 rounded-xl" data-testid="scenario-form-error">{error}</p>}

            {/* Scenario Details */}
            <Card className="border-slate-200/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-600">Scenario Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Name (English)</Label>
                    <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g., High Net Worth Client" data-testid="scenario-name-input" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Name (Hindi) — optional</Label>
                    <Input value={form.name_hi} onChange={(e) => setForm((f) => ({ ...f, name_hi: e.target.value }))}
                      placeholder="Optional Hindi name" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Description (English)</Label>
                  <textarea className="flex w-full rounded-xl border border-slate-200 bg-transparent px-3 py-2 text-sm mt-1 min-h-[80px] focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                    value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Describe the customer scenario and behavior..." data-testid="scenario-desc-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Description (Hindi) — optional</Label>
                  <textarea className="flex w-full rounded-xl border border-slate-200 bg-transparent px-3 py-2 text-sm mt-1 min-h-[60px] focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                    value={form.description_hi} onChange={(e) => setForm((f) => ({ ...f, description_hi: e.target.value }))}
                    placeholder="Optional Hindi description..." />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Difficulty</Label>
                  <Select value={form.difficulty} onValueChange={(v) => setForm((f) => ({ ...f, difficulty: v }))}>
                    <SelectTrigger className="mt-1" data-testid="scenario-difficulty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="mt-1" data-testid="scenario-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIES).map(([id, label]) => (
                        <SelectItem key={id} value={id}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Customer Profile */}
            <Card className="border-[#2563EB]/20 bg-[#2563EB]/[0.02]">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-[#2563EB] flex items-center gap-2">
                  <User className="w-4 h-4" /> Customer Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Age</Label>
                    <Input type="number" value={form.customer_profile.age}
                      onChange={(e) => updateProfile("age", e.target.value)}
                      className="mt-1" data-testid="profile-age-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Gender</Label>
                    <Select value={form.customer_profile.gender} onValueChange={(v) => updateProfile("gender", v)}>
                      <SelectTrigger className="mt-1" data-testid="profile-gender-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Location</Label>
                    <Input value={form.customer_profile.location}
                      onChange={(e) => updateProfile("location", e.target.value)}
                      placeholder="e.g., Mumbai" className="mt-1" data-testid="profile-location-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Profession</Label>
                    <Input value={form.customer_profile.profession}
                      onChange={(e) => updateProfile("profession", e.target.value)}
                      placeholder="e.g., Business Owner" className="mt-1" data-testid="profile-profession-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Job Role</Label>
                    <Input value={form.customer_profile.job_role}
                      onChange={(e) => updateProfile("job_role", e.target.value)}
                      placeholder="e.g., Managing Director" className="mt-1" data-testid="profile-jobrole-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Salary / Income</Label>
                  <Input value={form.customer_profile.salary}
                    onChange={(e) => updateProfile("salary", e.target.value)}
                    placeholder="e.g., 50+ LPA" className="mt-1" data-testid="profile-salary-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Family Details</Label>
                  <Input value={form.customer_profile.family_details}
                    onChange={(e) => updateProfile("family_details", e.target.value)}
                    placeholder="e.g., Wife, 2 adult children (25 and 28), 1 grandchild" className="mt-1" data-testid="profile-family-input" />
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-xl btn-premium" data-testid="save-scenario-btn">
              {saving ? "Saving..." : editing ? "Update Scenario" : "Create Scenario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
